/** "Refresh equipment": imports the CEC Solar Equipment Lists (PV modules, inverters, batteries) on a light worker;
 * at most one an hour, and weekly by itself (services/permit_equipment.py). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, unwrap } from "../../api/client";
import { Alert, Badge, Button, Card, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { useFmt } from "./format";

const LISTS: Record<string, MessageKey> = {
  pv_module: "admin.refreshEquipment.listPvModules",
  inverter: "admin.refreshEquipment.listInverters",
  battery: "admin.refreshEquipment.listBatteries",
};
const TONES = { succeeded: "ok", partial: "warn", failed: "danger", queued: "info", running: "info" } as const;

export function RefreshEquipment() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { date } = useFmt();
  const status = useQuery({
    queryKey: ["equipment-refresh"],
    queryFn: () => unwrap(api.GET("/v1/admin/permit-equipment-refresh")),
    refetchInterval: (q) => (["queued", "running"].includes(q.state.data?.latest?.status ?? "") ? 5000 : false),
  });
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/permit-equipment-refresh")),
    onSuccess: (data) => {
      qc.setQueryData(["equipment-refresh"], data);
      toast(t("admin.refreshEquipment.queued"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const s = status.data;
  const run = s?.latest;
  const counts = Object.entries(s?.counts ?? {});
  const runValues = run && {
    trigger: run.trigger === "weekly" ? "weekly" : run.requested_by ? "person" : "staff",
    who: run.requested_by ?? "",
    created: date(run.created_at, true),
    finished: date(run.finished_at, true),
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <Card
        title={t("admin.refreshEquipment.title")}
        description={t("admin.refreshEquipment.description", { weekly: s?.weekly === false ? "off" : "on" })}
        actions={
          <Button icon={<RefreshCw />} loading={start.isPending} disabled={!s || !!s.next_allowed_at} onClick={() => start.mutate()}>
            {t("admin.refreshEquipment.refresh")}
          </Button>
        }
      >
        {status.error ? (
          <Alert tone="danger">{errorMessage(status.error)}</Alert>
        ) : s ? (
          <div className="stack" style={{ gap: 8 }}>
            <span className="small">
              {counts.length
                ? counts.map(([k, n]) => t("admin.refreshEquipment.countBySource", { source: k, count: n })).join(" · ")
                : t("admin.refreshEquipment.noEquipment")}
            </span>
            {run && runValues ? (
              <>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <Badge tone={TONES[run.status]}>{t("admin.refreshEquipment.runStatus", { status: run.status })}</Badge>
                  <span className="small">
                    {run.finished_at ? t("admin.refreshEquipment.runFinished", runValues) : t("admin.refreshEquipment.runStarted", runValues)}
                  </span>
                </div>
                {run.sources.map((x) => (
                  <div key={x.source} className="small">
                    <FormattedMessage
                      id="admin.refreshEquipment.sourceResult"
                      values={{ list: LISTS[x.source] ? t(LISTS[x.source]) : x.source, message: <span dir="auto">{x.message}</span>, b: (c) => <strong>{c}</strong> }}
                    />
                  </div>
                ))}
              </>
            ) : (
              <span className="small faint">{t("admin.refreshEquipment.noRun")}</span>
            )}
            {s.next_allowed_at && <span className="small faint">{t("admin.refreshEquipment.nextAllowed", { at: date(s.next_allowed_at, true) })}</span>}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
